import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../../config';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';

export default function StravaCallbackPage() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const stravaStatus = params.get('strava'); // ok | error | ...

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [needsEmail, setNeedsEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        setError('');

        const res = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || 'Not authenticated');
        }
        const data = await res.json();
        if (cancelled) return;
        setUser(data.user);
        setNeedsEmail(Boolean(data.needs_email));
        setEmail(data.user?.email || '');
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || 'Failed to complete Strava login');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // Even if stravaStatus is not ok, try /me (useful if user refreshed)
    run();
    return () => {
      cancelled = true;
    };
  }, [stravaStatus]);

  // If the user is already connected and no email completion is needed,
  // send them back to where they were before OAuth (best effort).
  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (needsEmail) return;
    if (error) return;

    let redirectTo = '/';
    try {
      const stored = localStorage.getItem('runcoach_post_oauth_redirect');
      if (stored && typeof stored === 'string') redirectTo = stored;
      localStorage.removeItem('runcoach_post_oauth_redirect');
    } catch {
      // ignore
    }

    // Prevent looping back to this page.
    if (redirectTo.includes('/strava/callback')) redirectTo = '/';

    // Replace to avoid leaving a useless "callback" entry in history.
    window.location.replace(redirectTo);
  }, [loading, user, needsEmail, error]);

  const handleCancel = async () => {
    try {
      await logout();
      window.location.href = '/';
    } catch (e) {
      console.error(e);
      window.location.href = '/';
    }
  };

  const onSaveEmail = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/complete-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to save email');
      }
      setUser(data.user);
      setNeedsEmail(false);
    } catch (e2) {
      setError(e2?.message || 'Failed to save email');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-white rounded-xl shadow border border-gray-100">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('wizard.connect_strava')}</h1>

      {loading ? (
        <p className="text-gray-600">{t('plan.loading')}</p>
      ) : (
        <>
          {stravaStatus && stravaStatus !== 'ok' ? (
            <div className="mb-4 p-3 rounded bg-yellow-50 border border-yellow-200 text-yellow-900 text-sm">
              {t('wizard.strava_status')} <span className="font-mono">{stravaStatus}</span>
            </div>
          ) : null}

          {error ? (
            <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-900 text-sm">
              {error}
            </div>
          ) : null}

          {user ? (
            <div className="mb-4 text-sm text-gray-700">
              {t('wizard.connected_athlete')} <span className="font-mono">{String(user.strava_athlete_json?.firstname || '')} {String(user.strava_athlete_json?.lastname || '')}</span>
            </div>
          ) : null}

          {needsEmail ? (
            <form onSubmit={onSaveEmail} className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">{t('wizard.email_label')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder={t('wizard.email_placeholder')}
                  required
                />
                <p className="mt-2 text-xs text-gray-500">
                  {t('wizard.email_required_desc')}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-blue-600 text-white font-bold py-2 px-4 rounded-lg shadow hover:bg-blue-700 transition disabled:opacity-60"
                >
                  {saving ? t('wizard.saving_email') : t('wizard.save_email')}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
                >
                  {t('wizard.cancel_email')}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-3">
              <div className="p-3 rounded bg-green-50 border border-green-200 text-green-900 text-sm">
                {t('wizard.connected_redirect')}
              </div>
              <a
                href="/"
                className="inline-block bg-gray-900 text-white font-bold py-2 px-4 rounded-lg shadow hover:bg-black transition"
              >
                {t('wizard.go_home')}
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
}


